import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Typography,
  Avatar,
  Space,
  Input,
  Button,
  Upload,
  Menu,
  Dropdown,
  message,
  Divider,
  Row,
  Col,
  Spin,
  Tooltip,
  Image,
} from "antd";
import {
  UserOutlined,
  RobotOutlined,
  ClockCircleOutlined,
  SendOutlined,
  PaperClipOutlined,
  DeleteOutlined,
  MoreOutlined,
  CloseOutlined,
  DeleteTwoTone,
  PlusCircleOutlined,
  SmileFilled,
  FileImageFilled,
  PictureOutlined,
  LoadingOutlined,
  ReloadOutlined,
  PhoneOutlined,
  MailOutlined,
  ArrowLeftOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { styles } from "../../pages/playground/styles";
import { useAppDispatch } from "../../hooks/reduxHooks";
import {
  generateReplyApi,
  fetchChatHistory,
  chatDeleteAPI,
} from "../../services/playground.service";
import SingleChatItem from "../../pages/playground/sections/ChatWindowSection/SingleChatItem";
import { imageUploadApi } from "../../services/knowledgebase.service";
import "../../pages/playground/sections/ChatWindowSection/index.css";
import SingleChatItemChannel from "./SingleChatItemChannel";
import { sendAssistantMessageApi } from "../../services/channels.service";
import { transformString } from "../../helpers/channelNameTransformation";
import dayjs from "dayjs"; // Import dayjs for date handling
import { useNavigate } from "react-router-dom";
import { logout } from "../../store/slices/profile.slice.js";
import { checkBalance } from "../../helpers/checkBalance.js";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

// Safe YouTube URL checker for SingleChatItemChannel component
// This function should be exported or copied to SingleChatItemChannel.jsx
const isYouTubeVideo = (url) => {
  // First check if url is a string
  if (!url || typeof url !== "string") {
    return false;
  }

  // Now safely check if it's a YouTube URL
  return url.includes("youtube.com") || url.includes("youtu.be");
};

// Date Separator Component
const DateSeparator = ({ date }) => {
  // Format the date appropriately
  const formatDate = (dateString) => {
    const today = dayjs().startOf("day");
    const yesterday = today.subtract(1, "day");
    const messageDate = dayjs(dateString).startOf("day");

    if (messageDate.isSame(today)) {
      return "Today";
    } else if (messageDate.isSame(yesterday)) {
      return "Yesterday";
    } else {
      // For older dates, format as "Jan 1, 2025"
      return messageDate.format("MMM D, YYYY");
    }
  };

  return (
    <div
      className="date-separator"
      style={{
        display: "flex",
        justifyContent: "center",
        margin: "16px 0",
        width: "100%",
      }}
    >
      <span
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.06)",
          padding: "4px 12px",
          borderRadius: "16px",
          fontSize: "12px",
          color: "#888",
          fontWeight: "500",
        }}
      >
        {formatDate(date)}
      </span>
    </div>
  );
};

const ChatWindowSection = ({
  customerInfo,
  responseData,
  setResponseData,
  setVisible,
  setDrawerData,
  selectedCustomerId,
  setRefreshCustomerList,
  isMobile,
  onBack,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [spinning, setSpinning] = useState(false);
  const [loadingSendMsng, setLoadingSendMsng] = useState(false);

  // Loading state
  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [fileList, setFileList] = useState([]);
  const [fileURL, setFileURL] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);

  // Response page number metaData from response
  const [conversationsMeta, setConversationsMeta] = useState();

  // Reference to chat container for scrolling
  const chatContainerRef = useRef(null);

  // Store selectedCustomerId in a ref to ensure we always have the latest value
  const selectedCustomerIdRef = useRef(null);

  // New states for infinite scrolling
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const lastScrollPositionRef = useRef(0);

  // New state to track if user has intentionally scrolled
  const [hasScrolled, setHasScrolled] = useState(false);

  // Update the ref whenever selectedCustomerId changes
  useEffect(() => {
    selectedCustomerIdRef.current = selectedCustomerId;
  }, [selectedCustomerId]);

  useEffect(() => {
    if (selectedCustomerId) {
      setSpinning(true);
      fetchChatHis();
    }
  }, [selectedCustomerId]);

  // Scroll to bottom whenever conversations change, but only if we're not loading more at the top
  useEffect(() => {
    if (shouldScrollToBottom) {
      // Check if the latest message has media content
      const hasRecentMedia = conversations?.length > 0 &&
        conversations[conversations.length - 1]?.some(msg =>
          msg?.media_ids && msg?.media_ids?.length > 0
        );

      // Add delay if there are images to allow them to load
      if (hasRecentMedia) {
        setTimeout(scrollToBottom, 200);
      } else {
        scrollToBottom();
      }
    }
  }, [conversations, shouldScrollToBottom]);

  // Track which page is currently being loaded to prevent duplicate requests
  const loadingPageRef = useRef(null);

  // Add scroll event listener with debounce to prevent multiple rapid calls
  useEffect(() => {
    // Debounce function to prevent multiple calls in quick succession
    let scrollTimeout = null;

    const handleScroll = () => {
      const container = chatContainerRef.current;
      if (!container) return;

      // Store the current scroll position
      const currentScrollTop = container.scrollTop;

      // Check if user is scrolling up (away from the bottom)
      if (
        currentScrollTop <
        container.scrollHeight - container.clientHeight - 50
      ) {
        setHasScrolled(true);
      } else {
        // If user is at the bottom of the chat, they're not trying to view older messages
        setHasScrolled(false);
      }

      lastScrollPositionRef.current = currentScrollTop;

      // Clear any existing timeout to debounce the scroll event
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Set a new timeout to handle the scroll event
      scrollTimeout = setTimeout(() => {
        // Check if user has scrolled to the top (with a small threshold)
        if (currentScrollTop <= 30 && !isLoadingMore && hasMore) {
          // Make sure we're not already loading the next page
          const nextPage = page + 1;
          if (loadingPageRef.current !== nextPage) {
            loadMoreConversations();
          }
        }
      }, 250); // 250ms debounce
    };

    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
      // Clear the timeout on cleanup
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [isLoadingMore, hasMore, page, selectedCustomerId]); // Added selectedCustomerId to dependencies

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  const loadMoreConversations = () => {
    // Get the latest selectedCustomerId from the ref
    const currentCustomerId = selectedCustomerIdRef.current;
    const nextPage = page + 1;

    // Add safeguard to prevent API call if customer ID is missing or if already loading this page
    if (
      isLoadingMore ||
      !hasMore ||
      !currentCustomerId ||
      loadingPageRef.current === nextPage
    ) {
      console.log("Skipping loadMoreConversations:", {
        isLoadingMore,
        hasMore,
        currentCustomerId,
        currentlyLoadingPage: loadingPageRef.current,
        attemptedPage: nextPage,
      });
      return;
    }

    // Mark this page as currently being loaded to prevent duplicate requests
    loadingPageRef.current = nextPage;

    // Disable auto-scroll when loading more
    setShouldScrollToBottom(false);
    setIsLoadingMore(true);

    // Save the current scroll height to restore position later
    const scrollHeight = chatContainerRef.current?.scrollHeight || 0;

    dispatch(
      fetchChatHistory({
        params: {
          user_id: currentCustomerId, // Use the ref value here
          page_number: nextPage,
        },
        finalCallback: () => {
          setIsLoadingMore(false);
          // Clear the loading page reference when done
          loadingPageRef.current = null;

          // After new content is loaded, maintain the scroll position
          setTimeout(() => {
            if (chatContainerRef.current) {
              const newScrollHeight = chatContainerRef.current.scrollHeight;
              const heightDifference = newScrollHeight - scrollHeight;
              chatContainerRef.current.scrollTop =
                heightDifference > 0 ? heightDifference : 0;
            }
          }, 50);
        },
        successCallback: (response) => {
          if (!response.responseData || response.responseData.length === 0) {
            setHasMore(false);
            return;
          }

          setConversations((prevConversations) => {
            return [...response.responseData, ...prevConversations];
          });

          setPage(nextPage);
        },
        failureCallback: (error) => {
          console.error("Error loading more conversations:", error);
          setHasMore(false);
        },
      })
    );
  };

  const fetchChatHis = () => {
    // Make sure we have a valid customer ID
    if (!selectedCustomerIdRef.current) {
      console.log("No customer ID available for fetchChatHis");
      setSpinning(false);
      setLoading(false);
      return;
    }

    // Reset pagination data when fetching initial history
    setPage(1);
    setHasMore(true);
    setShouldScrollToBottom(true);
    setHasScrolled(false); // Reset scrolled state when fetching chat history

    dispatch(
      fetchChatHistory({
        params: {
          user_id: selectedCustomerIdRef.current,
          page_number: 1,
        },
        finalCallback: () => {
          setSpinning(false);
          setLoading(false);
          // Ensure scroll to bottom after loading conversations
          setTimeout(scrollToBottom, 100);
        },
        successCallback: (response) => {
          setConversations(response?.responseData || []);
          setConversationsMeta(response?.responseMeta);

          // Check if there might be more data to load
          if (!response?.responseData || response.responseData.length === 0) {
            setHasMore(false);
          }
        },
        failureCallback: (error) => {
          console.log("Error:", error);
          setHasMore(false);
        },
      })
    );
  };

  const sendMessage = (urls) => {
    // Check if we have a valid customer ID
    if (!selectedCustomerIdRef.current) {
      message.error("Cannot send message: Customer ID is missing");
      setLoadingSendMsng(false);
      return;
    }

    const newUrls = urls?.map((item) => item?.url);

    // When sending a message, we always want to scroll to bottom
    setShouldScrollToBottom(true);
    setHasScrolled(false); // Reset scroll state when sending a new message

    // Create new user message
    const newUserMessage = {
      role: "assistant",
      content: userInput,
      created_at: new Date().toISOString(),
      reply_urls: newUrls,
    };

    // Update conversations
    setConversations((prev) => {
      const updatedConversations = [...prev];
      if (updatedConversations?.length > 0) {
        updatedConversations[updatedConversations?.length - 1].push(
          newUserMessage
        );
      } else {
        updatedConversations.push([newUserMessage]);
      }
      return updatedConversations;
    });

    // Reset input and image preview
    setUserInput("");
    setImagePreview([]);

    const data = {
      user_id: selectedCustomerIdRef.current,
      content: userInput,
      media_url: urls,
      channel:
        customerInfo?.channel.charAt(0).toLowerCase() +
        customerInfo?.channel.slice(1),
    };

    dispatch(
      sendAssistantMessageApi({
        data: data,
        finalCallback: () => {
          setRefreshCustomerList(true);
          setLoadingSendMsng(false);
        },
        successCallback: (response) => {
          console.log("RESPONSE sendAssistantMessageApi -> ", response);
        },
        failureCallback: (error) => {
          message.error(
            error.response.data.detail.message || "Failed to send message"
          );
        },
      })
    );
  };

  const handleSendMessage = async () => {
    const balance = await checkBalance();

    if (balance < 1) {
      // Show an error message when balance is zero
      message.error(
        "You don't have enough credits. Please recharge your account."
      );
      return;
    }

    if (!userInput?.trim() && (!fileList || fileList?.length === 0)) return;

    // Check if we have a valid customer ID
    if (!selectedCustomerIdRef.current) {
      message.error("Cannot send message: Customer ID is missing");
      return;
    }

    console.log("imagePreview", fileList);
    setLoadingSendMsng(true);
    console.log("User Input -> ", userInput?.trim());

    if (fileList && fileList?.length > 0) {
      const formData = new FormData();
      fileList.forEach((file) => {
        formData.append("files", file.originFileObj);
      });

      dispatch(
        imageUploadApi({
          data: formData,
          params: { source: "test" },
          finalCallback: () => {
            // setLoadingSendMsng(false);
          },
          successCallback: (response) => {
            const urls = response?.data?.map((item) => item);
            setFileURL(urls);
            sendMessage(urls); // Now pass the uploaded image URLs
            setFileList([]);
          },
          failureCallback: (error) => {
            console.error("Image Upload Error", error);
            message.error("Failed to upload images. Please try again.");
            setLoadingSendMsng(false);
          },
        })
      );
    } else {
      sendMessage([]);
    }
  };

  // Helper function to validate if a file is a valid image
  const isValidImageFile = (file) => {
    const validExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
    const validMimeTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/bmp", "image/webp"];

    // Get file extension (handle files without extensions)
    const fileNameParts = file.name.split(".");
    const fileExtension = fileNameParts.length > 1 ? fileNameParts[fileNameParts.length - 1].toLowerCase() : "";

    // Check MIME type as primary validation
    const mimeType = file.originFileObj?.type || file.type;

    // File is valid if either extension or MIME type is valid
    const isValidExtension = fileExtension && validExtensions.includes(fileExtension);
    const isValidMimeType = mimeType && validMimeTypes.includes(mimeType);

    return isValidExtension || isValidMimeType;
  };

  // Async function to validate if a file can be read as an image
  const validateImageFile = (file) => {
    return new Promise((resolve) => {
      // First check basic validation
      if (isValidImageFile(file)) {
        resolve(true);
        return;
      }

      // If basic validation fails, try to read the file as an image
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          console.log("File validated as image through Image object:", file.name);
          resolve(true);
        };
        img.onerror = () => {
          console.log("File failed Image object validation:", file.name);
          resolve(false);
        };
        img.src = e.target.result;
      };

      reader.onerror = () => {
        console.log("File failed FileReader validation:", file.name);
        resolve(false);
      };

      reader.readAsDataURL(file.originFileObj);
    });
  };

  // Handle file change
  const handleFileChange = async ({ fileList }) => {
    const file = fileList[fileList?.length - 1]; // Get last selected file

    if (file && file.originFileObj) {
      const isValid = await validateImageFile(file);

      if (isValid) {
        console.log("fileList", fileList);
        setFileList(fileList);
        const newPreviews = [];

        fileList.forEach((file) => {
          if (file.originFileObj) {
            const reader = new FileReader();
            reader.onload = (event) => {
              newPreviews.push(event.target.result);

              // Only update state once after all files are processed
              if (newPreviews?.length === fileList?.length) {
                setImagePreview(newPreviews);
              }
            };
            reader.readAsDataURL(file.originFileObj);
          }
        });
      } else {
        message.error(
          "Only image files (jpg, jpeg, png, gif, bmp, webp) are allowed."
        );
        // Filter files using async validation
        const validFiles = [];
        for (const f of fileList) {
          if (await validateImageFile(f)) {
            validFiles.push(f);
          }
        }
        setFileList(validFiles);
      }
    } else {
      message.error("File upload failed. Please try again.");
    }
  };

  // Remove an image preview
  const removeImagePreview = (index) => {
    setFileList((prevFileList) =>
      prevFileList.filter((_, idx) => idx !== index)
    );
    setImagePreview((prevState) => prevState.filter((_, idx) => idx !== index));
  };

  // Render messages with date separators
  const renderMessagesWithDateSeparators = (conversations) => {
    if (!conversations || conversations.length === 0) return [];

    const result = [];
    let currentDate = null;

    conversations.flatMap((conversation, convIndex) => {
      const sortedMessages = [...conversation].sort((a, b) => {
        const timeA = new Date(a.created_at || 0).getTime();
        const timeB = new Date(b.created_at || 0).getTime();
        return timeA - timeB;
      });

      sortedMessages.forEach((message, msgIndex) => {
        // Skip messages without created_at timestamp
        if (!message.created_at) {
          result.push(
            <SingleChatItemChannel
              key={`msg-no-date-${msgIndex}-${convIndex}`}
              message={message}
              convIndex={convIndex}
              msgIndex={msgIndex}
              setVisible={setVisible}
              setResponseData={setResponseData}
              setDrawerData={setDrawerData}
              isYouTubeVideo={isYouTubeVideo} // Pass the safe function to the component
              customerInfo={customerInfo}
              fetchChatHis={fetchChatHis}
              setRefreshCustomerList={setRefreshCustomerList}
            />
          );
          return;
        }

        // Get message date without time (start of day)
        const messageDate = dayjs(message.created_at).startOf("day");

        // If this is a new date, add a separator
        if (!currentDate || !messageDate.isSame(currentDate)) {
          currentDate = messageDate;
          result.push(
            <DateSeparator
              key={`date-${message.created_at}-${convIndex}-${msgIndex}`}
              date={message.created_at}
            />
          );
        }

        // Add the message
        result.push(
          <SingleChatItemChannel
            key={`msg-${message.created_at}-${msgIndex}-${convIndex}`}
            message={message}
            convIndex={convIndex}
            msgIndex={msgIndex}
            setVisible={setVisible}
            setResponseData={setResponseData}
            setDrawerData={setDrawerData}
            isYouTubeVideo={isYouTubeVideo} // Pass the safe function to the component
            customerInfo={customerInfo}
            fetchChatHis={fetchChatHis}
            setRefreshCustomerList={setRefreshCustomerList}
          />
        );
      });
    });

    return result;
  };

  // Check if a conversation is selected
  if (!selectedCustomerId || !customerInfo) {
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#ffffff",
        }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "48px 24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <MessageOutlined
            style={{
              fontSize: "48px",
              color: "#bfbfbf",
            }}
          />
          <Title level={4} style={{ color: "#595959", margin: 0 }}>
            No Conversation Selected
          </Title>
          <Paragraph
            style={{
              color: "#8c8c8c",
              margin: 0,
              maxWidth: "300px",
              fontSize: "14px",
            }}
          >
            Please select a conversation from the list to get started
          </Paragraph>
        </div>
      </div>
    );
  }

  const chatControlSize = isMobile ? 40 : 48;
  const chatControlIconSize = isMobile ? 20 : 24;
  const chatInputPadding = isMobile ? "10px 12px" : "12px 16px";
  const chatInputMinHeight = isMobile ? "40px" : "48px";
  const chatInputLineHeight = isMobile ? "20px" : "24px";
  const chatInputRadius = isMobile ? "20px" : "24px";
  const chatControlGap = isMobile ? "8px" : "10px";

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px",
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
        }}
      >
            <div
              style={{
                display: "flex",
                gap: "4px",
                alignItems: "center",
              }}
            >
              {isMobile && onBack && (
                <Button
                  type="text"
                  icon={<iconify-icon icon="fa6-solid:arrow-left" width="16" height="32"></iconify-icon>}
                  onClick={onBack}
                  style={{ padding: 4, marginRight: 8, display: "flex", alignItems: "center", justifyContent: "center" }}
                />
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <div style={{ display: "flex", flexDirection: "Column" }}>
                  <Title level={4} style={{ margin: 0 }}>
                    {customerInfo?.customer_name ?? (
                      <span style={{ color: "#999999" }}>N/A</span>
                    )}
                  </Title>
                  {customerInfo?.channel && (
                    <span style={{ fontSize: "11px", color: "#999", textTransform: "capitalize" }}>
                      {customerInfo.channel}
                    </span>
                  )}
                </div>
                {customerInfo?.profile_name && (
                  <span style={{ fontSize: "12px", color: "#999" }}>
                    {customerInfo.profile_name}
                  </span>
                )}
              </div>

              {(customerInfo?.channel === "Website" && customerInfo?.email) ||
                (customerInfo?.channel === "Whatsapp" &&
                  customerInfo?.customer_id) ? (
                <Tooltip
                  title={
                    customerInfo?.channel === "Whatsapp" ? (
                      <div
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          navigator.clipboard.writeText(
                            customerInfo.customer_id
                          );
                          message.success("Phone number copied to clipboard!");
                        }}
                      >
                        {"+" + customerInfo?.customer_id || ""}
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "12px",
                            color: "#1890ff",
                          }}
                        >
                          (Click to copy)
                        </span>
                      </div>
                    ) : (
                      customerInfo?.channel === "Website" && (
                        <div
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            navigator.clipboard.writeText(customerInfo.email);
                            message.success(
                              "Email address copied to clipboard!"
                            );
                          }}
                        >
                          {customerInfo?.email || ""}
                          <span
                            style={{
                              marginLeft: "8px",
                              fontSize: "12px",
                              color: "#1890ff",
                            }}
                          >
                            (Click to copy)
                          </span>
                        </div>
                      )
                    )
                  }
                  color="#ffffff"
                  overlayInnerStyle={{
                    color: "#000000",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  }}
                >
                  <Button
                    type="default"
                    icon={
                      customerInfo?.channel === "Whatsapp" ? (
                        <PhoneOutlined />
                      ) : (
                        customerInfo?.channel === "Website" && <MailOutlined />
                      )
                    }
                    onClick={() => {
                      if (customerInfo?.channel === "Whatsapp") {
                        navigator.clipboard.writeText(
                          customerInfo?.customer_id || ""
                        );
                        message.success("Phone number copied to clipboard!");
                      } else if (customerInfo?.channel === "Website") {
                        navigator.clipboard.writeText(
                          customerInfo?.email || ""
                        );
                        message.success("Email address copied to clipboard!");
                      } else {
                        message.info("No phone number or email available");
                      }
                    }}
                  />
                </Tooltip>
              ) : (
                <></>
              )}
            </div>

            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchChatHis();
              }}
              disabled={loading}
            />
          </div>
        {/* </div> */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <div
            ref={chatContainerRef}
            className="chat-messages"
            style={{
              flex: 1,
              overflowY: "auto",
              padding: isMobile ? "16px 12px" : "24px 16px",
              minHeight: 0,
            }}
          >
            {/* Loading indicator at the top when fetching more conversations */}
            {hasMore && (
              <div
                style={{
                  textAlign: "center",
                  height: "30px",
                  marginBottom: "10px",
                }}
              >
                <Spin
                  indicator={<LoadingOutlined spin />}
                  spinning={isLoadingMore}
                />
              </div>
            )}

            {/* "No more messages" */}
            {!hasMore && conversations.length > 0 && (
                <div
                  style={{
                    textAlign: "center",
                    color: "#9aa0a6",
                    fontSize: "12px",
                    fontWeight: 500,
                    margin: "10px 0 20px 0",
                  }}
                >
                  No more messages
                </div>
              )}

            {/* Render messages with date separators */}
            {renderMessagesWithDateSeparators(conversations)}
          </div>

          {/* Image Preview Section */}
          {imagePreview?.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: "16px",
                flexWrap: "wrap",
                padding: "8px 24px",
                backgroundColor: "#f9f9f9",
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <Image.PreviewGroup>
                {imagePreview?.map((image, index) => (
                  <div key={index} style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Image
                      src={image}
                      alt={`Preview ${index}`}
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "8px",
                        border: "solid 1px #E5E7EB",
                        objectFit: "cover",
                        cursor: "pointer",
                      }}
                      preview={{
                        mask: <div style={{ fontSize: "12px" }}>Click to view</div>
                      }}
                    />
                    <Button
                      type="text"
                      shape="circle"
                      icon={<DeleteOutlined style={{ color: "#333", fontSize: 12 }} />}
                      onClick={() => removeImagePreview(index)}
                      style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        zIndex: 1,
                        minWidth: 24,
                        width: 24,
                        height: 24,
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        border: "1px solid rgba(0,0,0,0.1)",
                        borderRadius: "50%",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    />
                    <div style={{ marginTop: "4px", textAlign: "center", maxWidth: "80px" }}>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={fileList[index]?.name || `Image ${index + 1}`}
                      >
                        {fileList[index]?.name || `Image ${index + 1}`}
                      </div>
                    </div>
                  </div>
                ))}
              </Image.PreviewGroup>
            </div>
          )}

          {/* Chat Input Section */}
          <div
            className="chat-input"
            style={{
              padding: isMobile ? "12px" : "16px",
            }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: chatControlGap,
                width: "100%",
              }}
            >
              <Tooltip title="Upload Image">
                <Upload
                  fileList={fileList}
                  onChange={handleFileChange}
                  beforeUpload={() => false}
                  showUploadList={false}
                  multiple
                  accept="image/*"
                >
                  <Button
                    // icon={}
                    type="text"
                    style={{
                      height: chatControlSize,
                      width: chatControlSize,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      color: "#666",
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      border: "1px solid rgba(0,0,0,0.1)",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <iconify-icon
                      icon="uil:image-upload"
                      width={chatControlIconSize}
                      height={chatControlIconSize}
                    ></iconify-icon>
                  </Button>
                </Upload>
              </Tooltip>

              <TextArea
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Type your message..."
                autoSize={{ minRows: 1, maxRows: 3 }}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={loading || !selectedCustomerIdRef.current}
                className="ant-input chat-composer-textarea"
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "#f5f5f5",
                  resize: "none",
                  padding: chatInputPadding,
                  borderRadius: chatInputRadius,
                  lineHeight: chatInputLineHeight,
                  minHeight: chatInputMinHeight,
                }}
              />

              <Tooltip title="Send Message">
                <Button
                  type="primary"
                  // icon={}
                  onClick={handleSendMessage}
                  loading={
                    loadingSendMsng
                      ? { icon: <LoadingOutlined style={{ fontSize: 20 }} spin /> }
                      : false
                  }
                  disabled={!selectedCustomerIdRef.current}
                  shape="circle"
                  style={{
                    height: chatControlSize,
                    width: chatControlSize,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  {!loadingSendMsng && (
                    <iconify-icon
                      icon="majesticons:send-line"
                      width={chatControlIconSize}
                      height={chatControlIconSize}
                    ></iconify-icon>
                  )}
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
      {/* </div> */}
    </div>
  );
};

export default ChatWindowSection;
